import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import {
  trans,
  ACCESS_REQUEST_PENDING,
  ACCESS_REQUEST_SELECT_REASON,
  ACCESS_REQUEST_PLACEHOLDER,
  ACCESS_REQUEST_REASON_PLACEHOLDER,
  ACCESS_REQUEST_CONFLICT_ERROR,
  ACCESS_REQUEST_FORBIDDEN_ERROR,
  PDF_FAILED_TOAST,
  GENERATING_REPORT
} from '../../translator'

declare const $: (selector: string | HTMLElement) => { modal: (action: string) => void, dropdown: (settings?: object) => void }

interface AccessReason {
  id: string
  reason_text: string
}

interface AccessReasonsResponse {
  result: AccessReason[]
}

// fetchWithHandlingLoginRedirect resolves only on a 2xx response and rejects with
// the Response object for non-2xx, so error branches read `error.status`.
const XHR_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' }

export default class AccessRequestController extends Controller {
  static values = {
    apId: String,
    grantPending: Boolean
  }

  static targets = ['requestButton', 'exportButton', 'modal', 'reasonField', 'submitButton', 'error']

  declare readonly apIdValue: string
  declare readonly grantPendingValue: boolean
  declare readonly requestButtonTarget: HTMLButtonElement
  declare readonly hasRequestButtonTarget: boolean
  declare readonly exportButtonTarget: HTMLButtonElement
  declare readonly hasExportButtonTarget: boolean
  declare readonly modalTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly reasonFieldTarget: HTMLElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly hasSubmitButtonTarget: boolean
  declare readonly errorTarget: HTMLElement
  declare readonly hasErrorTarget: boolean

  private exportButtonHtml = ''

  connect (): void {
    if (this.grantPendingValue && this.hasRequestButtonTarget) {
      this.markPending()
    }
  }

  async openModal (): Promise<void> {
    this.clearError()
    await this.loadReasons()
    if (this.hasModalTarget) {
      $(this.modalTarget).modal('show')
    }
  }

  async submit (): Promise<void> {
    const reason = this.selectedReason()

    try {
      await fetchWithHandlingLoginRedirect(
        `/internal/api/associated-person/${this.apIdValue}/access-grant`,
        {
          method: 'POST',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify({ request_reason: reason })
        }
      )

      this.closeModal()
      this.markPending()
    } catch (error) {
      const status = (error as Response)?.status
      this.closeModal()

      if (status === 403) {
        this.showError(trans(ACCESS_REQUEST_FORBIDDEN_ERROR, {}, 'associated-persons'))
        return
      }

      this.showError(trans(ACCESS_REQUEST_CONFLICT_ERROR, {}, 'associated-persons'))
    }
  }

  async exportPdf (): Promise<void> {
    this.startExporting()

    try {
      const response = await fetchWithHandlingLoginRedirect(
        `/internal/api/associated-person/${this.apIdValue}/export-pdf`,
        { method: 'POST', headers: XHR_HEADERS }
      )
      const blob = await response.blob()
      this.downloadBlob(blob, this.filenameFromResponse(response))
    } catch {
      this.toast(trans(PDF_FAILED_TOAST, {}, 'associated-persons'))
    } finally {
      this.stopExporting()
    }
  }

  // Disable the button and swap in a spinner so the user gets immediate feedback —
  // PDF generation is server-side and can take a few seconds before the download starts.
  private startExporting (): void {
    if (!this.hasExportButtonTarget) {
      return
    }
    this.exportButtonHtml = this.exportButtonTarget.innerHTML
    this.exportButtonTarget.disabled = true
    this.exportButtonTarget.innerHTML =
      `<span class="spinner-border spinner-border-sm mr-1" aria-hidden="true"></span> ${trans(GENERATING_REPORT, {}, 'associated-persons')}`
  }

  private stopExporting (): void {
    if (!this.hasExportButtonTarget) {
      return
    }
    this.exportButtonTarget.disabled = false
    this.exportButtonTarget.innerHTML = this.exportButtonHtml
  }

  // The endpoint returns the PDF binary with a Content-Disposition: attachment header,
  // but an XHR POST does not trigger the browser's native download, so we do it from the blob.
  private downloadBlob (blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  private filenameFromResponse (response: Response): string {
    const disposition = response.headers?.get('Content-Disposition') ?? ''
    const match = /filename="?([^";]+)"?/.exec(disposition)

    return match?.[1] ?? 'associated-person.pdf'
  }

  private async loadReasons (): Promise<void> {
    try {
      const response = await fetchWithHandlingLoginRedirect(
        '/internal/api/associated-persons/access-reasons',
        { headers: XHR_HEADERS }
      )
      const data = await response.json() as AccessReasonsResponse
      this.renderReasonField(data.result ?? [])
    } catch {
      this.renderReasonField([])
    }
  }

  private renderReasonField (reasons: AccessReason[]): void {
    if (reasons.length === 0) {
      this.reasonFieldTarget.innerHTML =
        `<textarea class="form-control" rows="3" placeholder="${trans(ACCESS_REQUEST_REASON_PLACEHOLDER, {}, 'associated-persons')}"></textarea>`
      this.reasonFieldTarget.addEventListener('input', () => { this.updateSubmitState() })
      this.updateSubmitState()
      return
    }

    const items = reasons
      .map(reason => `<div class="item" data-value="${escapeHtml(reason.reason_text)}">${escapeHtml(reason.reason_text)}</div>`)
      .join('')

    const label = trans(ACCESS_REQUEST_SELECT_REASON, {}, 'associated-persons')
    const placeholder = trans(ACCESS_REQUEST_PLACEHOLDER, {}, 'associated-persons')
    this.reasonFieldTarget.innerHTML =
      '<div class="form-group mb-0">' +
      `<label class="form-control-label">${label}</label>` +
      '<div class="ui fluid search selection dropdown">' +
      '<input type="hidden">' +
      '<i class="dropdown icon"></i>' +
      `<div class="default text">${placeholder}</div>` +
      `<div class="menu">${items}</div>` +
      '</div>' +
      '</div>'

    $(this.reasonFieldTarget.querySelector('.ui.dropdown') as HTMLElement)
      .dropdown({ onChange: () => { this.updateSubmitState() } })
    this.updateSubmitState()
  }

  private selectedReason (): string {
    const field = this.reasonFieldTarget.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    return field?.value ?? ''
  }

  private updateSubmitState (): void {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = this.selectedReason().trim() === ''
    }
  }

  private markPending (): void {
    if (!this.hasRequestButtonTarget) {
      return
    }
    this.requestButtonTarget.disabled = true
    this.requestButtonTarget.textContent = trans(ACCESS_REQUEST_PENDING, {}, 'associated-persons')
  }

  private closeModal (): void {
    if (this.hasModalTarget) {
      $(this.modalTarget).modal('hide')
    }
  }

  private showError (message: string): void {
    if (!this.hasErrorTarget) {
      return
    }
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('d-none')
  }

  private clearError (): void {
    if (!this.hasErrorTarget) {
      return
    }
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('d-none')
  }

  private toast (message: string): void {
    if (typeof $ !== 'undefined' && typeof ($ as unknown as { notify?: unknown }).notify === 'function') {
      ($ as unknown as { notify: (opts: object, settings: object) => void }).notify(
        { message },
        { type: 'danger', placement: { from: 'bottom', align: 'right' } }
      )
      return
    }
    this.showError(message)
  }
}
